import {
  ArrowDownward,
  ArrowDropDown,
  ArrowUpward,
  CalendarTodayOutlined,
  FlagOutlined,
  FormatListBulletedOutlined,
  ScheduleOutlined,
} from '@mui/icons-material';
import {
  Button,
  Divider,
  darken,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  styled,
  useTheme,
} from '@mui/material';
import { useCallback, useState } from 'react';

import { tOrder } from '@frontend/locales';

import { OrdersGroupByFieldModel, OrdersSortDirection } from '../hooks';

const OptionMenuItem = styled(MenuItem)(({ theme }) => ({
  '&.Mui-selected': {
    color: theme.palette.success.dark,
    backgroundColor: theme.palette.success.light,

    '&:hover': {
      backgroundColor: darken(theme.palette.success.light, 0.1),
    },

    '& .MuiListItemIcon-root': {
      color: theme.palette.success.dark,
    },
  },
}));

export interface OrderGroupingSelectProps {
  onGroupByChange(groupBy: OrdersGroupByFieldModel): void;
  onSortDirectionChange(sort: OrdersSortDirection): void;
  selected: OrdersGroupByFieldModel;
  sortDirection: OrdersSortDirection;
}

const groupByOptions: {
  id: OrdersGroupByFieldModel;
  label: () => string;
  icon: React.ReactElement;
}[] = [
  {
    id: 'STATUS',
    label: tOrder.fields.groupByStatus,
    icon: <FlagOutlined fontSize="small" />,
  },
  {
    id: 'DATE',
    label: tOrder.fields.groupByDate,
    icon: <CalendarTodayOutlined fontSize="small" />,
  },
  {
    id: 'NONE',
    label: tOrder.fields.groupByNone,
    icon: <FormatListBulletedOutlined fontSize="small" />,
  },
  {
    id: 'TIMESLOT',
    label: tOrder.fields.groupByTimeslot,
    icon: <ScheduleOutlined fontSize="small" />,
  },
];

const sortOptions: {
  id: OrdersSortDirection;
  label: () => string;
  icon: React.ReactElement;
}[] = [
  {
    id: 'ASC',
    label: tOrder.fields.sortAscending,
    icon: <ArrowUpward fontSize="small" />,
  },
  {
    id: 'DESC',
    label: tOrder.fields.sortDescending,
    icon: <ArrowDownward fontSize="small" />,
  },
];

export const OrderGroupingSelect = (props: OrderGroupingSelectProps) => {
  const { selected, sortDirection, onGroupByChange, onSortDirectionChange } =
    props;
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const showSort = selected === 'NONE';

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const selectedGroupLabel =
    groupByOptions.find((o) => o.id === selected)?.label() ?? selected;
  const selectedSortLabel =
    sortOptions.find((o) => o.id === sortDirection)?.label() ?? sortDirection;

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ArrowDropDown />}
        onClick={handleOpen}
        sx={{
          height: 48,
          color: theme.palette.text.secondary,
          borderColor: theme.palette.divider,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minWidth: showSort ? 260 : 160,

          '&:hover': {
            borderColor: theme.palette.divider,
          },
        }}
      >
        {showSort
          ? `${selectedGroupLabel} · ${selectedSortLabel}`
          : selectedGroupLabel}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 5,
          },
        }}
      >
        <Stack
          direction="row"
          divider={
            showSort ? <Divider orientation="vertical" flexItem /> : undefined
          }
        >
          <MenuList sx={{ minWidth: 160, py: 0 }}>
            {groupByOptions.map((option) => {
              const isSelected = option.id === selected;
              return (
                <OptionMenuItem
                  key={option.id}
                  selected={isSelected}
                  onClick={() => {
                    onGroupByChange(option.id);
                    handleClose();
                  }}
                >
                  <ListItemIcon>{option.icon}</ListItemIcon>
                  <ListItemText>{option.label()}</ListItemText>
                </OptionMenuItem>
              );
            })}
          </MenuList>

          {showSort && (
            <MenuList sx={{ minWidth: 140, py: 0 }}>
              {sortOptions.map((option) => {
                const isSelected = option.id === sortDirection;
                return (
                  <OptionMenuItem
                    key={option.id}
                    selected={isSelected}
                    onClick={() => {
                      onSortDirectionChange(option.id);
                      handleClose();
                    }}
                  >
                    <ListItemIcon>{option.icon}</ListItemIcon>
                    <ListItemText>{option.label()}</ListItemText>
                  </OptionMenuItem>
                );
              })}
            </MenuList>
          )}
        </Stack>
      </Popover>
    </>
  );
};
